import React, { useState, useEffect, useRef } from "react";
import {
  Tabs,
  Tab,
  Box,
  useMediaQuery,
  Paper,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  ClickAwayListener,
} from "@mui/material";
import {
  Workspaces as TeamsIcon,
  EmojiPeople as FollowersIcon,
  Inbox as InboxIcon,
  LocalFireDepartment as LeadsIcon,
  Visibility as LogsIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarMonthIcon,
  PeopleAlt as NetworkIcon,
  Timeline as ActivityIcon,
  ExpandMore as ExpandMoreIcon,
  AssignmentInd as AssignmentIndIcon,
} from "@mui/icons-material";

const menuData = {
  network: [
    { label: "Inbox", icon: <InboxIcon />, value: "inbox" },
    { label: "Assigned", icon: <AssignmentIndIcon />, value: "assigned" },
    { label: "Followers", icon: <FollowersIcon />, value: "followers" },
    { label: "Leads", icon: <LeadsIcon />, value: "leads" },
  ],
  activity: [
    { label: "Appointments", icon: <CalendarMonthIcon />, value: "appointments" },
    { label: "Logs", icon: <LogsIcon />, value: "logs" },
  ],
};

const tabData = [
  { label: "Dashboard", icon: <TeamsIcon />, value: "dashboard" },
  {
    label: "Network",
    icon: <NetworkIcon />,
    value: "network",
    hasDropdown: true,
    menu: "network"
  },
  {
    label: "Activity",
    icon: <ActivityIcon />,
    value: "activity",
    hasDropdown: true,
    menu: "activity"
  },
  { label: "Settings", icon: <SettingsIcon />, value: "settings" },
];

const TabsSelector = ({ value, onChange, settings }) => {
  const isMobile = useMediaQuery("(max-width: 840px)");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const menuRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const activeTabRef = useRef(null);
  const isMouseInDropdownAreaRef = useRef(false);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleGlobalMouseMove = (event) => {
      if (!activeMenu || !anchorEl) return;

      const menuElement = document.querySelector('.MuiMenu-paper');

      const isOverTab = anchorEl.contains(event.target);
      const isOverMenu = menuElement && menuElement.contains(event.target);

      const isInDropdownArea = isOverTab || isOverMenu;

      if (isMouseInDropdownAreaRef.current && !isInDropdownArea) {
        startCloseTimer();
      }

      if (!isMouseInDropdownAreaRef.current && isInDropdownArea) {
        cancelCloseTimer();
      }

      isMouseInDropdownAreaRef.current = isInDropdownArea;
    };

    if (activeMenu) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [activeMenu, anchorEl]);

  const playTabClickSound = () => {
    if (settings?.["Miscellaneous Sounds"] !== false) {
      const audio = new Audio("/sounds/tab_click.mp3");
      audio.play().catch((err) =>
        console.error("Error playing tab click sound:", err)
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    const clickedTab = tabData.find(tab => tab.value === newValue);
    if (!clickedTab?.hasDropdown) {
      playTabClickSound();
      onChange(event, newValue);
    }
  };

  const handleMenuOpen = (event, menuName) => {
    event.stopPropagation();
    activeTabRef.current = event.currentTarget;
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuName);
    isMouseInDropdownAreaRef.current = true;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    activeTabRef.current = null;
    isMouseInDropdownAreaRef.current = false;
  };

  const handleMenuItemClick = (event, menuItemValue) => {
    playTabClickSound();
    onChange(event, menuItemValue);
    handleMenuClose();
  };

  const startCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      handleMenuClose();
    }, 500);
  };

  const cancelCloseTimer = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const glassBg = (theme) =>
    `linear-gradient(135deg, ${alpha("#181A1B", 0.92)}, ${alpha(
      "#23272f",
      0.85
    )})`;

  return (
    <Box
      sx={{
        my: isMobile ? 0 : 1,
        width: "100%",
        position: isMobile ? "fixed" : "static",
        bottom: isMobile ? 0 : "auto",
        left: 0,
        zIndex: 1200,
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Paper
        elevation={isMobile ? 8 : 0}
        sx={{
          pointerEvents: "auto",
          borderRadius: isMobile ? "0px" : "16px",
          px: isMobile ? 0 : 2,
          mx: isMobile ? 0 : 0,
          py: isMobile ? 0 : 1,
          bgcolor: glassBg,
          backdropFilter: "blur(16px)",
          boxShadow: isMobile
            ? "0 8px 32px 0 rgba(0,0,0,0.2)"
            : "0 4px 20px 0 rgba(0,0,0,0.1)",
          maxWidth: "100%",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <Tabs
          value={value}
          onChange={handleTabChange}
          variant={"standard"}
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{
            sx: {
              height: "0px",
              borderRadius: "8px",
              transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
              bottom: 0,
            },
          }}
          sx={{
            height: isMobile ? 56 : 80,
            ".MuiTabs-flexContainer": {
              gap: isMobile ? 0 : 1.5,
            },
            ".MuiTabs-indicator": {
              bottom: 0,
            },
          }}
        >
          {tabData.map((tab, index) => {
            const isSelected = value === tab.value ||
              (tab.menu === "network" && ["inbox", "assigned", "followers", "leads"].includes(value)) ||
              (tab.menu === "activity" && ["appointments", "logs"].includes(value));

            return (
              <Tooltip title={isMobile ? tab.label : ""} key={index} placement="top">
                <Tab
                  icon={tab.icon}
                  iconPosition="top"
                  label={
                    isMobile
                      ? ""
                      : (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {tab.label}
                          {tab.hasDropdown && (
                            <ExpandMoreIcon
                              sx={{
                                ml: 0.5,
                                fontSize: 18,
                                transition: "transform 0.3s",
                                transform: activeMenu === tab.menu ? "rotate(180deg)" : "rotate(0)",
                              }}
                            />
                          )}
                        </Box>
                      )
                  }
                  onClick={tab.hasDropdown ? (e) => handleMenuOpen(e, tab.menu) : undefined}
                  value={tab.value}
                  sx={{
                    minWidth: isMobile ? 56 : 150,
                    mt: isMobile ? 0.5 : 0,
                    px: isMobile ? 4 : 2.5,
                    py: isMobile ? 0 : 1.5,
                    color: isSelected ? "#98FF98" : "#d0d8e1",
                    fontWeight: 600,
                    fontFamily: "Geist Mono, sans-serif",
                    letterSpacing: 1.5,
                    textShadow: "0 1px 2px rgba(15,15,15,0.4)",
                    fontSize: isMobile ? 12 : 14,
                    borderRadius: isMobile ? "8px" : "12px",
                    transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                    "& .MuiTab-iconWrapper": {
                      mb: isMobile ? 0 : 0.75,
                      fontSize: isMobile ? 24 : 28,
                      transition: "all 0.3s ease",
                      color: "inherit",
                    },
                    "&.Mui-selected": {
                      color: "#98FF98",
                      boxShadow: isSelected ? `0 0 0 1px ${alpha("#98FF98", 0.3)}` : "none",
                      "& .MuiTab-iconWrapper": {
                        transform: "translateY(-2px)",
                        filter: "drop-shadow(0 0 3px rgba(152, 255, 152, 0.5))",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: isMobile ? "6px" : "8px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "30%",
                        backgroundColor: "#98FF98",
                        borderRadius: "2px",
                      }
                    },
                    "&:hover": {
                      color: isSelected ? "#98FF98" : "#fff",
                      background: alpha("#98FF98", isSelected ? 0.2 : 0.1),
                      "& .MuiTab-iconWrapper": {
                        transform: isSelected ? "translateY(-2px) scale(1.2)" : "scale(1.2)",
                      },
                    },
                    outline: "none",
                    "&.Mui-focusVisible": {
                      boxShadow: `0 0 0 2px ${alpha("#98FF98", 0.5)}`,
                    },
                  }}
                />
              </Tooltip>
            );
          })}
        </Tabs>
      </Paper>

      <ClickAwayListener onClickAway={handleMenuClose}>
        <div ref={menuRef}>
          <Menu
            anchorEl={anchorEl}
            open={activeMenu === "network"}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            MenuListProps={{
              'aria-labelledby': 'network-button',
              autoFocusItem: false,
            }}
            PopoverClasses={{
              root: "dropdown-menu-popover",
            }}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "12px",
                bgcolor: glassBg,
                backdropFilter: "blur(16px)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                mt: 0.5,
              },
            }}
            disablePortal={false}
            keepMounted={false}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={true}
          >
            {menuData.network.map((item) => (
              <MenuItem
                key={item.value}
                onClick={(e) => handleMenuItemClick(e, item.value)}
                selected={value === item.value}
                sx={{
                  borderRadius: "8px",
                  m: 0.5,
                  py: 1,
                  px: 2,
                  color: value === item.value ? "#98FF98" : "#d0d8e1",
                  "&:hover": {
                    bgcolor: alpha("#98FF98", 0.15),
                  },
                  "&.Mui-selected": {
                    bgcolor: alpha("#98FF98", 0.2),
                    color: "#98FF98",
                    fontWeight: "bold",
                  },
                }}
              >
                <ListItemIcon sx={{ color: value === item.value ? "#98FF98" : "#d0d8e1" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={anchorEl}
            open={activeMenu === "activity"}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            MenuListProps={{
              'aria-labelledby': 'activity-button',
              autoFocusItem: false,
            }}
            PopoverClasses={{
              root: "dropdown-menu-popover",
            }}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "12px",
                bgcolor: glassBg,
                backdropFilter: "blur(16px)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                mt: 0.5,
              },
            }}
            disablePortal={false}
            keepMounted={false}
            disableAutoFocus={true}
            disableEnforceFocus={true}
            disableRestoreFocus={true}
          >
            {menuData.activity.map((item) => (
              <MenuItem
                key={item.value}
                onClick={(e) => handleMenuItemClick(e, item.value)}
                selected={value === item.value}
                sx={{
                  borderRadius: "8px",
                  m: 0.5,
                  py: 1,
                  px: 2,
                  color: value === item.value ? "#98FF98" : "#d0d8e1",
                  "&:hover": {
                    bgcolor: alpha("#98FF98", 0.15),
                  },
                  "&.Mui-selected": {
                    bgcolor: alpha("#98FF98", 0.2),
                    color: "#98FF98",
                    fontWeight: "bold",
                  },
                }}
              >
                <ListItemIcon sx={{ color: value === item.value ? "#98FF98" : "#d0d8e1" }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Menu>
        </div>
      </ClickAwayListener>
    </Box>
  );
};

export default TabsSelector;
